<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import store from '../../../store';
import SkiDatePicker from '../../../components/ski/skipass/SkiDatePicker';
import numberMixin from '../../../mixins/numberMixin';
import TwCopyAndTranslateModal from '../../../components/TwCopyAndTranslateModal';

export default {
  name: 'SkiPassSeasonPriceSettings',
  components: {
    SkiDatePicker,
    TwCopyAndTranslateModal,
  },
  mixins: [ numberMixin ],
  props: {
    seasonId: {
      type: [ Number, String ],
      default: null,
    },
    new: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      seasonItem: Object.assign({}, this.season),
      availableNetOrGross: [
        {
          id: 'net',
          name: 'Net',
        },
        {
          id: 'gross',
          name: 'Gross',
        },
      ],
      keyCardPaymentTypes: [
        {
          id: 'mandatory',
          name: 'Mandatory',
        },
        {
          id: 'deposit',
          name: 'Deposit',
        },
      ],
      keyCardPaymentTypeDeposit: 'deposit',
      doRedirectToNewSeason: false,
      copyAndTranslateModalOpen: false,
    };
  },
  computed: {
    ...mapGetters({
      suppliers: 'skipass/getSkiSuppliers',
      availableSalesRules: 'skipass/getSalesTypes',
    }),
    ...mapState({
      season: state => state.skipass.season,
      xmlChannels: state => state.twenty.xmlChannels,
      seasonItemsLoading: state => state.skipass.season.itemsLoading,
      seasonItemsError: state => state.skipass.season.itemsError,
      seasonSaveLoading: state => state.skipass.season.saveLoading,
      seasonSaveError: state => state.skipass.season.saveError,
      suppliersLoading: state => state.skipass.suppliers.itemsLoading,
      suppliersError: state => state.skipass.suppliers.itemsError,
      salesTypesLoading: state => state.skipass.salesTypes.itemsLoading,
      salesTypesError: state => state.skipass.salesTypes.itemsError,
      domains: state => state.twenty.domains.items,
      selectedDomainId: state => state.ski.selectedDomainId,
    }),
    errorOccurred() {
      return this.seasonItemsError || this.seasonSaveError || this.salesTypesError || this.xmlChannels.itemsError;
    },
    selectSupplierLabel() {
      const totalSuppliers = Object.keys(this.suppliers).length;
      return totalSuppliers
        ? '(found '.concat(totalSuppliers.toString()).concat(' suppliers)')
        : '';
    },
    getCopyValues() {
      return [
        {
          globalModelId: this.seasonItem.id,
          globalModelColumnName: 'skipass_season_id',
          selectKeyOverrides: [ 'domain_id', 'skipass_season_id' ],
          tableName: 'domain_skipass_season',
          sourceDomainId: null,
          selectedFromDomain: null,
          fromLanguageIsoOverride: null,
          sourceIsoOverride: null,
          disableSourceIsoOverride: true,
          toDomains: [],
          shouldTranslate: false,
          shouldCopySeo: false,
          fieldNamesAndValuesToCopyAndTranslate: [
            { information_text: this.seasonItem.informationText },
            { name: this.seasonItem.name },
            { domain_id: null },
            { skipass_season_id: this.seasonItem.id },
          ],
          title: `skipass_seasons: ${this.seasonItem.name}`,
        },
      ];
    },
  },
  watch: {
    season: {
      deep: true,
      handler() {
        if (this.doRedirectToNewSeason
          && this.seasonItem.id === undefined
          && this.season.id !== undefined) {
          this.doRedirectToNewSeason = false;
          this.$router.push(`/ski/ski-pass/season/${this.season.id}`);
          this.$emit('newSeasonCreated', { seasonId: this.season.id });
        }
        this.seasonItem = Object.assign({}, this.season);
      },
    },
    selectedDomainId() {
      this.handleDomains();
    },
  },
  methods: {
    ...mapActions({
      loadSeason: 'skipass/seasonWithRelationsBySeasonIdLoad',
      loadSuppliers: 'skipass/suppliersLoad',
      loadAvailableSalesTypes: 'skipass/salesTypesLoad',
      setSeason: 'skipass/setSeason',
      createSeason: 'skipass/seasonCreate',
      saveSeason: 'skipass/seasonSave',
    }),
    handleDomains() {
      if (this.selectedDomainId !== null) {
        this.seasonItem.domains = this.seasonItem.domains || {};
        if (!this.seasonItem.domains[this.selectedDomainId]) {
          this.seasonItem.domains[this.selectedDomainId] = {
            domainId: this.selectedDomainId,
            name: '',
            informationText: '',
          };
        }
      }
    },
    allowedDates(date) {
      return date > this.seasonItem.arrivalFrom;
    },
    createOrUpdate() {
      if (this.seasonItem.id) {
        this.saveSeason(this.seasonItem);
      } else if (this.createSeason(this.seasonItem)) {
        this.doRedirectToNewSeason = true;
      }
    },
    onChange(payload) {
      if (payload.arrivalFrom && payload.arrivalFrom > this.seasonItem.arrivalTo) {
        this.setSeason({
          arrivalTo: payload.arrivalFrom,
        });
      }
      this.setSeason(payload);
    },
    onChangeDecimal(key, value) {
      const payload = {};
      payload[key] = this.$options.filters.numberToDots(value);
      this.onChange(payload);
    },
    formatDecimal(value) {
      return `${value}`.replace('.', ',');
    },
  },
  created() {
    this.handleDomains();
  },
  mounted() {
    if (this.season.id) {
      this.loadSeason(this.season.id);
    } else {
      this.seasonItem = Object.assign({}, this.season);
    }
    if (!this.suppliers.length) {
      this.loadSuppliers();
    }
    if (!this.availableSalesRules.length) {
      this.loadAvailableSalesTypes();
    }
  },
  beforeRouteEnter(to, from, next) {
    if (Object.keys(to.params).length === 0 || to.params.new !== undefined) {
      store.dispatch('skipass/seasonByIdLoad', undefined)
        .finally(() => {
          next();
        });
    }

    if (to.params.seasonId) {
      store.dispatch('skipass/seasonWithRelationsBySeasonIdLoad', to.params.seasonId)
        .finally(() => {
          next();
        });
    }
  },
};
</script>

<template>
  <v-container class="pa-0 pl-4 skipass-season" grid-list-xl>
    <h2 class="d-inline-block">
      {{ seasonItem.id
        ? $t('ski.skiPriceSettings.title')
        : $t('ski.skiPriceSettings.newSeason')
      }}
    </h2>
    <v-btn
      v-if="seasonItem.id"
      :loading="seasonItemsLoading"
      color="primary success"
      @click="copyAndTranslateModalOpen = !copyAndTranslateModalOpen"
    >
      Copy/translate
    </v-btn>
    <tw-copy-and-translate-modal
      v-if="copyAndTranslateModalOpen"
      :elements-to-handle="getCopyValues"
      :domains="domains"
    />
    <v-btn
      :loading="seasonItemsLoading"
      icon
      @click="loadSeason(season.id)"
    >
      <v-icon>cached</v-icon>
    </v-btn>
    <div class="text-center pa-1">
      <v-progress-linear
        v-if="seasonItemsLoading
          || seasonSaveLoading
          || suppliersLoading
          || salesTypesLoading
        "
        :indeterminate="true"
      />
    </div>
    <v-alert
      :value="errorOccurred"
      color="error"
      icon="warning"
      outline
    >
      <span v-html="errorOccurred"/>
    </v-alert>
    <v-flex xs6>
      <v-text-field
        :value="seasonItem.name"
        label="Global Name"
        @input="onChange({ name: $event })"
      />
      <v-text-field
        v-if="selectedDomainId !== null"
        v-model="seasonItem.domains[selectedDomainId].name"
        label="Domain Name"
      />
    </v-flex>
    <v-layout>
      <v-flex xs3 md3>
        <v-switch
          :label="$t('ski.skiArea.showOnWeb')"
          :input-value="seasonItem.webActive"
          @change="onChange({ webActive: $event })"
        />
      </v-flex>
      <v-flex xs3 md3>
        <v-switch
          :label="$t('ski.skiPriceSettings.pricesActive')"
          :input-value="seasonItem.active"
          @change="onChange({ active: $event })"
        />
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex class="pa-2" xs3 md3>
        <ski-date-picker
          :date.sync="seasonItem.arrivalFrom"
          :title="$t('ski.datePicker.from')"
          @onChange="onChange({ arrivalFrom: $event })"
        />
      </v-flex>
      <v-flex class="pa-2" xs3 md3>
        <ski-date-picker
          :disabled="seasonItem.arrivalFrom === undefined"
          :allowed-dates="allowedDates"
          :date.sync="seasonItem.arrivalTo"
          :title="$t('ski.datePicker.to')"
          @onChange="onChange({ arrivalTo: $event })"
        />
      </v-flex>
    </v-layout>
    <h3 class="mt-2 mb-1">{{ $t('ski.skiPriceSettings.supplier') }}</h3>
    <v-layout>
      <v-flex sm5 xs5>
        <v-autocomplete
          :value="seasonItem.twentySupplierId"
          :items="this.suppliers"
          :loading="suppliersLoading"
          :disabled="suppliersLoading"
          :label="selectSupplierLabel"
          item-text="name"
          item-value="id"
          @change="onChange({ twentySupplierId: $event })"
        />
      </v-flex>
      <v-flex sm2 xs2>
        <v-select
          :value="seasonItem.netOrGross"
          :items="availableNetOrGross"
          :label="$t('ski.skiPriceSettings.netOrGross')"
          item-text="name"
          item-value="id"
          hide-details
          @change="onChange({ netOrGross: $event })"
        />
      </v-flex>
      <v-flex sm2 xs2>
        <v-text-field
          v-if="seasonItem.netOrGross === 'gross'"
          :value="seasonItem.commissionRatio"
          :placeholder="$t('ski.skiPriceSettings.commission')"
          :label="$t('ski.skiPriceSettings.percentageCommission')"
          type="Number"
          min="0"
          hide-details
          @change="onChange({ commissionRatio: $event })"
        />
        <v-text-field
          v-if="seasonItem.netOrGross === 'net'"
          :value="seasonItem.contributionRatio"
          :placeholder="$t('ski.skiPriceSettings.contribution')"
          :label="$t('ski.skiPriceSettings.percentageContribution')"
          type="Number"
          min="0"
          hide-details
          @change="onChange({ contributionRatio: $event })"
        />
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex sm3 xs3>
        <v-select
          :value="seasonItem.salesRule"
          :items="availableSalesRules"
          :loading="salesTypesLoading"
          :label="$t('ski.skiPriceSettings.rule')"
          item-text="name"
          item-value="id"
          hide-details
          @change="onChange({ salesRule: $event })"
        />
      </v-flex>
      <v-flex sm2 xs2>
        <v-text-field
          :value="formatDecimal(seasonItem.priceAdjustmentValue)"
          label="Price Adjustment Value"
          hint="This value is multiplied with all prices prior to any calculations"
          persistent-hint
          reverse
          @change="onChangeDecimal('priceAdjustmentValue', $event)"
        />
      </v-flex>
    </v-layout>
    <h3 class="mt-2 mb-1">{{ $t('ski.skiPriceSettings.keyCard') }}</h3>
    <v-layout>
      <v-flex sm3 xs3>
        <v-select
          :value="seasonItem.keyCardPaymentType"
          :items="keyCardPaymentTypes"
          :label="$t('ski.skiPriceSettings.keyCardPaymentType')"
          item-text="name"
          item-value="id"
          hide-details
          @change="onChange({ keyCardPaymentType: $event })"
        />
      </v-flex>
      <v-flex sm2 xs2>
        <v-text-field
          v-if="seasonItem.keyCardPaymentType !== keyCardPaymentTypeDeposit"
          :disabled="seasonItem.keyCardPaymentType === keyCardPaymentTypeDeposit"
          :value="seasonItem.keyCard"
          :placeholder="$t('ski.skiPriceSettings.keyCardPrice')"
          label="Key card price"
          reverse
          @change="onChange({ keyCard: $event })"
        />
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex xs2 md3>
        <v-text-field
          :value="seasonItem.bookDaysBeforeArrival"
          label="Book days before arrival"
          type="Number"
          min="0"
          hide-details
          @change="onChange({ bookDaysBeforeArrival: $event })"
        />
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex sm8>
        <v-textarea
          :value="seasonItem.informationText"
          label="Global Customer information text"
          box
          @change="onChange({ informationText: $event })"
        />
        <v-textarea
          v-if="selectedDomainId !== null"
          v-model="seasonItem.domains[selectedDomainId].informationText"
          label="Domain Customer information text"
          box
        />
      </v-flex>
    </v-layout>

    <v-layout>
      <v-flex sm8 xs8>
        <v-autocomplete
          v-model="seasonItem.xmlChannelId"
          :items="xmlChannels.items"
          :loading="xmlChannels.itemsLoading"
          :disabled="xmlChannels.itemsLoading"
          :error="xmlChannels.itemsError"
          label="XML Channel"
          item-text="webservice"
          item-value="id"
          class="mt-3"
          clearable
        />
      </v-flex>
      <v-flex sm4 xs4>
        <v-switch
          :input-value="seasonItem.xmlPriceUpdateActivated"
          label="Price update"
          @change="onChange({ xmlPriceUpdateActivated: $event })"
        />
      </v-flex>
    </v-layout>

    <div class="text-xs-right pr-3">
      <v-btn
        :disabled="seasonSaveLoading"
        color="primary"
        @click="createOrUpdate"
      >
        {{ seasonItem.id ? $t('buttons.save') : $t('buttons.create') }}
      </v-btn>
    </div>
  </v-container>
</template>

<style lang="scss">
  .skipass-season {
    .v-messages {
      display: none;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/ski/skipass/SkiPassSeasonPriceSettings.vue